// Once-per-session tracking_health_ping. Writes a single row to
// public.analytics_events via the anon client so we can compare hosting
// analytics ("1,000 visitors") against pixel-fired analytics and see
// whether traffic is executing JS, hitting the wrong domain, or breaking
// before tracking loads.
//
// Privacy: no name, email, phone, address, or customer document info.

import { supabase } from '@/integrations/supabase/client';
import { detectBrandProfile } from '@/lib/brandRegistry';
import { getGoogleTrackingConfig } from '@/lib/googleTracking';

const SESSION_FLAG = 'obc_tracking_health_pinged';

const BOT_UA_RE = /(bot|crawler|spider|crawling|headlesschrome|lighthouse|pingdom|monitor|preview|facebookexternalhit|slurp|baiduspider|bingpreview|yandex|ahrefs|semrush|mj12bot|dotbot|petalbot)/i;

const safeRead = <T,>(fn: () => T, fallback: T): T => {
  try { return fn(); } catch { return fallback; }
};

const getVisitorId = () =>
  safeRead(() => localStorage.getItem('obc_visitor_id') || '', '');
const getSessionId = () =>
  safeRead(() => sessionStorage.getItem('obc_session_id') || '', '');

const getQueryParam = (key: string): string | undefined => {
  try {
    const v = new URLSearchParams(window.location.search).get(key);
    return v || undefined;
  } catch { return undefined; }
};

export const fireTrackingHealthPing = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(SESSION_FLAG) === '1') return;
    sessionStorage.setItem(SESSION_FLAG, '1');
  } catch { /* private mode etc. */ }

  // Give third-party scripts a moment to register so the diagnostic is accurate.
  await new Promise((r) => setTimeout(r, 4000));

  const w = window as any;
  const cfg = (() => {
    try { return getGoogleTrackingConfig(); } catch { return { ga4MeasurementId: '', googleAdsId: '', gtmId: '' } as any; }
  })();
  const brand = (() => {
    try { return detectBrandProfile(); } catch { return null as any; }
  })();

  const ua = navigator.userAgent || '';
  const visitorId = getVisitorId() || `anon_${Math.random().toString(36).slice(2, 12)}`;
  const sessionId = getSessionId() || undefined;

  const consentState = (() => {
    try {
      const arr = (w.dataLayer || []) as any[];
      const last = [...arr].reverse().find((e) => e && e[0] === 'consent');
      return last ? 'observed' : 'granted_default';
    } catch { return 'unknown'; }
  })();

  const params = {
    site_domain: window.location.hostname,
    source_domain: window.location.hostname,
    source_brand: brand?.brand ?? null,
    microsite_brand: brand?.brand ?? null,
    microsite_topic: brand?.primaryTopic ?? null,
    parent_brand: brand?.parentBrand ?? null,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    referrer: document.referrer || null,
    user_agent: ua.slice(0, 900),
    visitor_id: visitorId,
    session_id: sessionId ?? null,
    ga4_configured: !!cfg.ga4MeasurementId && typeof w.gtag === 'function',
    google_ads_configured: !!cfg.googleAdsId,
    gtm_loaded: !!cfg.gtmId && Array.isArray(w.dataLayer),
    meta_pixel_loaded: typeof w.fbq === 'function',
    clarity_loaded: typeof w.clarity === 'function',
    growth_hub_ready: !!w.obcTracker,
    consent_state: consentState,
    is_likely_bot: BOT_UA_RE.test(ua),
    tracking_source: 'client',
    utm_source: getQueryParam('utm_source') ?? null,
    utm_medium: getQueryParam('utm_medium') ?? null,
    utm_campaign: getQueryParam('utm_campaign') ?? null,
    gclid: getQueryParam('gclid') ?? null,
    gbraid: getQueryParam('gbraid') ?? null,
    wbraid: getQueryParam('wbraid') ?? null,
  };

  try {
    await supabase.from('analytics_events').insert({
      visitor_id: visitorId,
      session_id: sessionId,
      event_name: 'tracking_health_ping',
      page_path: window.location.pathname.slice(0, 500),
      page_url: window.location.href.slice(0, 2000),
      page_title: (document.title || '').slice(0, 300),
      referrer: (document.referrer || '').slice(0, 2000) || null,
      user_agent: ua.slice(0, 1000),
      event_params: params,
    });
  } catch {
    // Tracking must never break the website.
  }
};
